
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import ProductCard from '../../components/base/ProductCard';
import SEO from '../../components/SEO';
import { fetchFeaturedProducts, formatPrice, type Product } from '../../utils/api';
import { categories } from '../../data/categories';
import { absoluteUrl, getProductPath } from '../../utils/urls';

const homeCategoryCards = [
  { id: 'ridebeklaedning', description: 'Ridebukser, støvler, jakker og hjelme – sammenlign priser' },
  { id: 'staldinventar', description: 'Spande, dækkener, grimer og hverdagsudstyr til stalden' },
  { id: 'pleje', description: 'Børster, hovkroge, olie og shampoo til daglig hestepleje' },
  { id: 'sadler', description: 'Dressur, spring og western – sammenlign sadler efter disciplin' },
  { id: 'foder', description: 'Hestefoder, tilskud, vitaminer og mineraler' },
  { id: 'hestetilbehor', description: 'Trenser, dækkener, underlag og øvrigt hesteudstyr' }
];

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadFeaturedProducts() {
      try {
        const response = await fetchFeaturedProducts(8);
        setFeaturedProducts(response.data.products);
      } catch (error) {
        console.error('Failed to load featured products:', error);
      } finally {
        setLoading(false);
      }
    }
    loadFeaturedProducts();
  }, []);

  const featuredCategories = homeCategoryCards
    .map((card) => {
      const category = categories.find((item) => item.id === card.id);
      if (!category) return null;
      return {
        ...category,
        cardDescription: card.description
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Hesteudstyr og Rideudstyr – Sammenlign Priser"
        description="Sammenlign priser på sadler, ridebeklædning, staldinventar, pleje og foder fra danske forhandlere. EquiNord er et affiliate-overblik over hesteudstyr."
        keywords="hesteudstyr, rideudstyr, sadler, ridebeklædning, staldinventar, hestefoder, hestepleje, danmark"
        url={absoluteUrl('/')}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "EquiNord",
          "url": "https://equinord.dk",
          "description": "Sammenlign priser på hesteudstyr fra danske forhandlere"
        }}
      />
      <Header />
      <main>
      {/* Hero Section */}
      <section 
        className="relative h-[70vh] min-h-[28rem] max-h-[40rem] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/images/home-hero.jpg')`
        }}
      >
        <div className="text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Hesteudstyr – sammenlign priser
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto">
            Find sadler, ridebeklædning, staldinventar, pleje og foder fra danske forhandlere. EquiNord viser priserne; du køber i shoppen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" href="/sadler">
              Se sadler
            </Button>
            <Button variant="outline" size="lg" href="/ridebeklaedning">
              Se ridebeklædning
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Shop efter Kategori</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Find sadler, ridebeklædning og plejeudstyr ved at sammenligne priser fra danske forhandlere – fra <Link to="/sadler" className="text-emerald-600 hover:text-emerald-700 font-medium">sadler</Link> til <Link to="/pleje" className="text-emerald-600 hover:text-emerald-700 font-medium">daglig pleje</Link>.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredCategories.map((category) => (
              <Link
                key={category.id}
                to={category.path}
                className="group bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={category.heroImage}
                    alt={category.name}
                    width="400"
                    height="300"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white pr-4">
                    <h3 className="text-xl font-bold mb-2">{category.name}</h3>
                    <p className="text-sm opacity-90">{category.cardDescription}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Fremhævede Produkter</h2>
            <p className="text-xl text-gray-600">
              Håndplukkede produkter med særlige tilbud fra vores pålidelige partnere
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              <div className="col-span-4 text-center py-12">
                <p className="text-gray-600">Indlæser produkter...</p>
              </div>
            ) : featuredProducts.length === 0 ? (
              <div className="col-span-4 text-center py-12">
                <p className="text-gray-600">Ingen produkter tilgængelige lige nu.</p>
              </div>
            ) : (
              featuredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  href={getProductPath(product)}
                  name={product.title}
                  price={formatPrice(product.price, product.currency)}
                  originalPrice={product.old_price && product.old_price > product.price ? formatPrice(product.old_price, product.currency) : undefined}
                  image={product.image_url || 'https://via.placeholder.com/300x300?text=No+Image'}
                  vendor={product.shop || product.brand}
                />
              ))
            )}
          </div>
          
          <div className="text-center mt-12">
            <Button variant="outline" size="lg" href="/hestetilbehor">
              Se flere produkter
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Hvorfor Vælge EquiNord?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Pålidelige Partnere</h3>
                    <p className="text-gray-600">Vi samarbejder kun med velrenommerede brands og forhandlere kendt for kvalitet og pålidelighed. <Link to="/about" className="text-emerald-600 hover:text-emerald-700 font-medium">Læs mere om vores partnere</Link>.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-price-tag-3-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Bedste Priser</h3>
                    <p className="text-gray-600">Sammenlign priser på tværs af flere forhandlere for at sikre dig de bedste tilbud.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Ekspert Vejledning</h3>
                    <p className="text-gray-600">Sammenlign priser fra danske forhandlere. Læs <Link to="/blog" className="text-emerald-600 hover:text-emerald-700 font-medium">guides på bloggen</Link> eller se <Link to="/faq" className="text-emerald-600 hover:text-emerald-700 font-medium">FAQ</Link>.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative aspect-[6/5]">
              <img
                src="/images/home-trust.jpg"
                alt="Glad rytter med hest"
                width="600"
                height="500"
                className="rounded-lg shadow-lg object-cover object-top w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
}
